import { formatTime, parseLocalDate } from "../time_formatting";

/**
 * Custom element for displaying a time in the user's local timezone.
 *
 * <em-local-time format="compact" start="2025-08-02T09:12:00-04:00" end="2025-08-02T11:00:00-04:00" has-time="true" timezone="EDT">Sat, Aug 2 9:12am to 11am EDT</em-local-time>
 *
 * On render or change, the inner content of the tag is replaced with the time formatted in the user's local timezone.
 *
 * Attributes:
 * - format: The format of the time to display. This can be an extended strftime string, or one of the known format classes:
 *   - "detailed"
 *   - "compact"
 *   - "compact_time"
 * - start: The start time of the time range, in ISO 8601 format.
 * - end: The end time of the time range, in ISO 8601 format.
 * - has-time: Whether the time component of start and end is used
 */
export class EmLocalTime extends HTMLElement {
  static get observedAttributes() {
    return ["format", "start", "end", "has-time"];
  }

  constructor() {
    super();

    this.format = "compact";
    this.start = null;
    this.end = null;
    this.hasTime = false;

    this._childObserver = null;
    this._refreshScheduled = false;
  }

  connectedCallback() {
    this.startObserving();
    // MutationObserver does not fire for children that were inserted before observe(), so fire a refresh immediately.
    if (this.firstChild !== null) {
      this.scheduleRefresh();
    }
  }

  disconnectedCallback() {
    if (this._childObserver !== null) {
      this._childObserver.disconnect();
      this._childObserver = null;
    }
    this._refreshScheduled = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "format":
        this.format = newValue;
        break;
      case "start":
        this.start = newValue;
        break;
      case "end":
        this.end = newValue;
        break;
      case "has-time":
        this.hasTime = newValue === "true";
        break;
    }

    this.scheduleRefresh();
  }

  ensureChildObserver() {
    if (this._childObserver !== null) {
      return;
    }
    this._childObserver = new MutationObserver(() => {
      this.scheduleRefresh();
    });
  }

  startObserving() {
    this.ensureChildObserver();
    this._childObserver.observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  stopObserving() {
    this._childObserver?.disconnect();
  }

  scheduleRefresh() {
    if (this._refreshScheduled) {
      return;
    }
    this._refreshScheduled = true;
    queueMicrotask(() => {
      this._refreshScheduled = false;
      if (!this.isConnected) {
        return;
      }
      this.refreshInnerFromAttributes();
    });
  }

  // Refresh the inner content of the element based on the current state.
  // The child observer is disconnected to avoid a refresh loop.
  refreshInnerFromAttributes() {
    this.stopObserving();

    try {
      if (!this.start) {
        console.warn("em-local-time: start is required");
        return;
      }

      const parsedStart = parseLocalDate(this.start, !this.hasTime);
      if (!parsedStart) {
        console.warn(
          `em-local-time: start of ${this.start} is not a valid date`,
        );
        return;
      }

      const parsedEnd = parseLocalDate(this.end, !this.hasTime);
      if (this.end && !parsedEnd) {
        console.warn(`em-local-time: end of ${this.end} is not a valid date`);
        return;
      }

      this.innerHTML = formatTime(parsedStart, parsedEnd, this.format, {
        dateOnly: !this.hasTime,
      });
    } finally {
      this.startObserving();
    }
  }
}
